import dayjs from "dayjs";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { FRANCE_ADDRESS_ZIP_CODE_CITY } from "utils/api/graphql/queries/referentials";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  MILTIS_SOCIAL_REGIMES,
} from "utils/constants";
import { hundredYearsAgoStart } from "utils/helpers/date";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

function View({
  telecoms,
  contact,
  contract,
  onNext,
  onBack,
  deltaStart,
  territorial,
}) {
  const [franceAddressZipCodeCity] = useLazyQuery(FRANCE_ADDRESS_ZIP_CODE_CITY);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_MILTIS.ADHERENT_BIRTHDATE", {
    returnObjects: true,
  });
  const SOCIAL_REGIMES_TRANSLATION = t("B2C_MILTIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });

  const { handleSubmit, values, ...formProps } = useFormik({
    initialValues: {
      telecoms,
      birthDate: get(contact, "user.birthDate") || null,
      socialRegime: get(contact, "socialRegime") || null,
      issuanceDate: get(contract, "issuanceDate") || null,
      optMadelin: get(contact, "optMadelin") || false,
    },
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      { franceAddressZipCodeCity },
    ),
  });

  const isSelfEmployed =
    values.socialRegime === MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS;

  return (
    <Layout>
      <Header
        title={TRANSLATION.HEADER.TITLE}
        highlight={TRANSLATION.HEADER.HIGHLIGHT}
        subtitle={TRANSLATION.HEADER.SUBTITLE}
        onBack={onBack}
      />
      <div className="miltis-form">
        <div className="miltis-form__container">
          {/* Row 1: Code postal | Date de naissance */}
          <div className="miltis-form__row">
            <Input
              placeholder={TRANSLATION.FORM.ZIP_CODE.PLACEHOLDER}
              maxLength={5}
              label={TRANSLATION.FORM.ZIP_CODE.LABEL}
              {...bindInputProps({
                name: `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                values,
                ...formProps,
              })}
            />
            <Input
              type="date"
              format={DATE_FORMAT}
              placeholder={TRANSLATION.FORM.BIRTH_DATE.PLACEHOLDER}
              label={TRANSLATION.FORM.BIRTH_DATE.LABEL}
              disabledDate={(current) =>
                current > dayjs().subtract(18, "years").endOf("year") ||
                current < hundredYearsAgoStart
              }
              defaultPickerValue={
                !get(values, "birthDate") && dayjs().subtract(30, "years")
              }
              {...bindDateInputProps({
                name: "birthDate",
                values,
                ...formProps,
                onBlur: (value) => {
                  const major = dayjs().subtract(18, "years").endOf("year");
                  const date = dayjs(value, DATE_FORMAT);
                  if (date?.isValid() && date.isBefore(major)) {
                    formProps.setFieldValue(
                      "birthDate",
                      date ? date.add(2, "h").toISOString() : null,
                    );
                  }
                },
              })}
            />
          </div>

          {/* Row 2: Régime social | Date d'effet souhaitée */}
          <div className="miltis-form__row">
            <Select
              options={Object.values(MILTIS_SOCIAL_REGIMES)
                .filter(
                  (option) =>
                    !territorial ||
                    option !==
                      MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                )
                .map((option) => {
                  return {
                    label: SOCIAL_REGIMES_TRANSLATION[option] || option,
                    value: option,
                  };
                })}
              placeholder={TRANSLATION.FORM.SOCIAL_REGIME.PLACEHOLDER}
              label={TRANSLATION.FORM.SOCIAL_REGIME.LABEL}
              value={values.socialRegime}
              touched={get(formProps.touched, "socialRegime", false)}
              errors={get(formProps.errors, "socialRegime", "")}
              onChange={(value) => {
                formProps.setFieldValue("socialRegime", value);
                // Reset optMadelin when social regime changes
                if (
                  value !== MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS
                ) {
                  formProps.setFieldValue("optMadelin", false);
                }
              }}
              onBlur={() => {
                formProps.setFieldTouched("socialRegime", true);
              }}
            />
            <Input
              type="date"
              format={DATE_FORMAT}
              placeholder={TRANSLATION.FORM.EFFECTIVE_DATE.PLACEHOLDER}
              label={TRANSLATION.FORM.EFFECTIVE_DATE.LABEL}
              disabledDate={(current) =>
                current < dayjs().add(deltaStart, "days").startOf("day")
              }
              {...bindDateInputProps({
                name: "issuanceDate",
                values,
                ...formProps,
              })}
            />
          </div>

          {/* {isSelfEmployed && (
            <div className="miltis-madelin-checkbox">
              <Checkbox
                variant="madelin"
                checked={values.optMadelin || false}
                onChange={(checked) =>
                  formProps.setFieldValue("optMadelin", checked)
                }
              >
                <div className="miltis-madelin-checkbox__content">
                  <p className="miltis-madelin-checkbox__text">
                    {TRANSLATION.FORM.MADELIN.TEXT}
                  </p>
                  <p className="miltis-madelin-checkbox__subtext">
                    {TRANSLATION.FORM.MADELIN.SUBTEXT}
                  </p>
                </div>
              </Checkbox>
            </div>
          )} */}

          <ActionButtons
            disabled={
              !get(values, `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`) ||
              !values.birthDate ||
              !isEmpty(formProps.errors)
            }
            onNext={handleSubmit}
            // onBack={onBack}
          />
        </div>
      </div>
    </Layout>
  );
}

export default View;
